import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck, Upload, CheckCircle2, ShieldCheck, FileCheck2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { k: "vehicle_number", label: "Vehicle Number", req: true, ph: "e.g. MH01AB1234", up: true },
  { k: "chassis_no", label: "Chassis No.", ph: "" },
  { k: "engine_no", label: "Engine No.", ph: "" },
  { k: "national_permit_no", label: "National Permit Number", ph: "" },
  { k: "driver_dl_no", label: "Driver DL Number", ph: "" },
  { k: "driver_name", label: "Driver Name", ph: "" },
  { k: "driver_phone", label: "Driver Phone", ph: "" },
];

const UPLOADS = [
  { k: "rc_photo", label: "RC (Registration Certificate)" },
  { k: "permit_photo", label: "National Permit" },
  { k: "dl_photo", label: "Driver's Licence (DL)" },
];

export default function VendorDocsPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/vendor-docs/${token}`).then((r) => {
      setInfo(r.data);
      setForm(r.data.docs || {});
      if (r.data.status === "verified") setDone(true);
    }).catch((e) => setErr(apiErr(e)));
  }, [token]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (k, file) => {
    if (!file) return;
    setUploading(k);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/vendor-docs/${token}/upload`, fd);
      set(k, data.path);
      toast.success("File uploaded");
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(""); }
  };

  const submit = async () => {
    if (!(form.vehicle_number || "").trim()) return toast.error("Vehicle Number is required");
    setSubmitting(true);
    try {
      await api.post(`/vendor-docs/${token}`, {
        vehicle_number: form.vehicle_number || "", chassis_no: form.chassis_no || "", engine_no: form.engine_no || "",
        national_permit_no: form.national_permit_no || "", driver_dl_no: form.driver_dl_no || "",
        driver_name: form.driver_name || "", driver_phone: form.driver_phone || "",
        rc_photo: form.rc_photo || "", permit_photo: form.permit_photo || "", dl_photo: form.dl_photo || "",
      });
      setDone(true);
      toast.success("Documents submitted — thank you!");
    } catch (e) { toast.error(apiErr(e)); } finally { setSubmitting(false); }
  };

  if (err) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white grid place-items-center p-6">
        <div className="max-w-md text-center border border-neutral-800 p-8">
          <ShieldCheck className="h-10 w-10 text-red-500 mx-auto mb-3" />
          <h1 className="font-bold text-xl mb-2">Link Unavailable</h1>
          <p className="text-neutral-400 text-sm">{err}</p>
        </div>
      </div>
    );
  }
  if (!info) return <div className="min-h-screen bg-neutral-950 text-white grid place-items-center">Loading…</div>;

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <div className="max-w-2xl mx-auto p-5 sm:p-8">
        <div className="flex items-center gap-2 mb-1"><Truck className="h-6 w-6 text-orange-500" /><span className="font-black tracking-widest text-lg">ARROW LOGISTICS</span></div>
        <p className="text-neutral-400 text-sm mb-6">Vehicle &amp; Driver Document Submission</p>

        <div className="border border-neutral-800 bg-neutral-900 p-4 mb-6">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-[11px] uppercase tracking-wide text-neutral-500">Order</div><div className="font-mono font-bold text-orange-400">{info.order_number}</div></div>
            <div><div className="text-[11px] uppercase tracking-wide text-neutral-500">Route</div><div>{info.route}</div></div>
            {info.vendor_name && <div><div className="text-[11px] uppercase tracking-wide text-neutral-500">Transporter</div><div>{info.vendor_name}</div></div>}
            {info.move_date && <div><div className="text-[11px] uppercase tracking-wide text-neutral-500">Move Date</div><div>{info.move_date}</div></div>}
          </div>
        </div>

        {done ? (
          <div className="border border-emerald-700 bg-emerald-950/40 p-8 text-center" data-testid="vendor-docs-done">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
            <h2 className="font-bold text-xl mb-1">{info.status === "verified" ? "Documents Verified" : "Documents Submitted"}</h2>
            <p className="text-neutral-400 text-sm">{info.status === "verified" ? "Your documents have been verified by our team." : "Thank you. Our team will review your documents shortly. You may close this page."}</p>
          </div>
        ) : (
          <div className="space-y-5" data-testid="vendor-docs-form">
            <div className="grid sm:grid-cols-2 gap-3">
              {FIELDS.map((f) => (
                <div key={f.k} className="space-y-1">
                  <Label className="text-xs text-neutral-300">{f.label}{f.req && <span className="text-orange-500"> *</span>}</Label>
                  <Input className="rounded-none bg-neutral-900 border-neutral-700 h-11 text-white uppercase" value={form[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} placeholder={f.ph} data-testid={`vd-${f.k}`} />
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <div className="text-xs uppercase tracking-wide text-neutral-400 font-bold">Upload Photos</div>
              <div className="grid sm:grid-cols-3 gap-3">
                {UPLOADS.map((u) => (
                  <label key={u.k} className={`border border-dashed ${form[u.k] ? "border-emerald-600 bg-emerald-950/30" : "border-neutral-700 bg-neutral-900"} p-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-orange-500 transition-colors min-h-[120px] text-center`} data-testid={`vd-upload-${u.k}`}>
                    <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => upload(u.k, e.target.files?.[0])} />
                    {uploading === u.k ? <Loader2 className="h-6 w-6 animate-spin text-orange-500" /> : form[u.k] ? <FileCheck2 className="h-6 w-6 text-emerald-500" /> : <Upload className="h-6 w-6 text-neutral-500" />}
                    <span className="text-[11px] text-neutral-300">{u.label}</span>
                    <span className={`text-[10px] ${form[u.k] ? "text-emerald-400" : "text-neutral-500"}`}>{form[u.k] ? "Uploaded ✓ — tap to replace" : "Tap to upload"}</span>
                  </label>
                ))}
              </div>
            </div>

            <Button className="w-full rounded-none h-12 bg-orange-600 hover:bg-orange-700 font-bold uppercase tracking-wide" onClick={submit} disabled={submitting || !!uploading} data-testid="vd-submit">
              {submitting ? "Submitting…" : "Submit Documents"}
            </Button>
            <p className="text-[11px] text-neutral-500 text-center">No login required. Your submission goes directly to Arrow Logistics for review.</p>
          </div>
        )}
      </div>
    </div>
  );
}
